import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Clock, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Reveal, Overline } from "../components/site/Reveal";
import { sanitizeHtml } from "../lib/sanitize";
import FocalImage from "../components/FocalImage";

function fmtDate(d, lang) {
  try { return new Date(d).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function EventDetail() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => setEv(false));
  }, [id]);

  if (ev === false) return <div className="max-w-3xl mx-auto px-5 py-40 text-center font-display text-3xl text-primary/50">{t("Event not found.", "Événement introuvable.")}</div>;
  if (!ev) return <div className="min-h-[60vh] flex items-center justify-center font-display text-3xl text-primary/40 animate-pulse">GYBC</div>;

  const rsvp = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/member/rsvp", { event_id: id, origin_url: window.location.origin });
      if (data.requires_payment && data.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data.already) { toast.info(t("You're already registered for this event.", "Vous êtes déjà inscrite à cet événement.")); return; }
      toast.success(t("RSVP confirmed! See you there.", "Inscription confirmée ! À bientôt."));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Could not RSVP. Try again.", "Échec de l'inscription. Réessayez."));
    }
  };

  const tickets = [
    { name_en: "General Admission", name_fr: "Admission générale", price: ev.price, desc_en: "Access to the full event", desc_fr: "Accès à l'événement complet" },
    { name_en: "Member Rate", name_fr: "Tarif membre", price: ev.member_price, desc_en: "Discounted rate for GYBC members", desc_fr: "Tarif réduit pour les membres GYBC" },
  ];
  if (ev.vip_price > 0) tickets.push({ name_en: "VIP Table", name_fr: "Table VIP", price: ev.vip_price, desc_en: "Reserved table for 8 + perks", desc_fr: "Table réservée pour 8 + avantages" });

  const agenda = ev.agenda || [];

  return (
    <div data-testid="event-detail-page">
      <section className="relative h-[45vh] min-h-[340px] overflow-hidden">
        <FocalImage img={{ url: ev.image, ...(ev.image_focal || {}) }} alt="" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-5 md:px-8 pb-10 w-full text-primary-foreground">
            <button onClick={() => navigate("/events")} className="flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-primary-foreground mb-4"><ArrowLeft size={16} /> {t("All events", "Tous les événements")}</button>
            <span className="text-xs uppercase tracking-widest text-accent font-semibold">{ev.type}</span>
            <h1 className="font-display text-4xl md:text-6xl font-medium tracking-tight mt-2 max-w-4xl">{lang === "fr" ? ev.title_fr : ev.title_en}</h1>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <Reveal><Overline>{t("About this event", "À propos")}</Overline></Reveal>
          <p className="text-lg text-foreground/70 leading-relaxed mt-4">{lang === "fr" ? ev.description_fr : ev.description_en}</p>
          {(lang === "fr" ? ev.body_html_fr : ev.body_html_en) ? (
            <div className="prose-article mt-6" dangerouslySetInnerHTML={{ __html: sanitizeHtml(lang === "fr" ? ev.body_html_fr : ev.body_html_en) }} />
          ) : null}

          <div className="mt-10">
            <Overline>{t("Meet the speaker", "La conférencière")}</Overline>
            <div className="mt-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center font-display text-2xl text-secondary font-semibold">{(ev.speaker || "G")[0]}</div>
              <div>
                <p className="font-display text-2xl font-semibold text-primary">{ev.speaker}</p>
                <p className="text-secondary font-medium text-sm">{lang === "fr" ? ev.speaker_role_fr : ev.speaker_role_en}</p>
              </div>
            </div>
          </div>

          {agenda.length > 0 && (
            <div className="mt-10">
              <Overline>{t("Agenda", "Programme")}</Overline>
              <div className="mt-4 space-y-4">
                {agenda.map((item, i) => (
                  <div key={i} className="flex gap-5 border-b border-primary/10 pb-4">
                    <span className="font-semibold text-secondary w-24 shrink-0">{item.time}</span>
                    <span className="text-foreground/80">{lang === "fr" ? item.item_fr : item.item_en}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-card border border-primary/10 rounded-3xl p-7 sticky top-24">
            <div className="space-y-3 text-sm text-primary/80 pb-5 border-b border-primary/10">
              <span className="flex items-center gap-2"><Calendar size={16} className="text-secondary" /> {fmtDate(ev.date, lang)}</span>
              <span className="flex items-center gap-2"><Clock size={16} className="text-secondary" /> {ev.time}</span>
              <span className="flex items-center gap-2"><MapPin size={16} className="text-secondary" /> {lang === "fr" ? ev.venue_fr : ev.venue_en}</span>
            </div>
            <p className="font-display text-2xl font-semibold text-primary mt-5 mb-3">{t("Get your tickets", "Vos billets")}</p>
            <div className="space-y-3">
              {tickets.map((tk, i) => (
                <div key={i} className="border border-primary/10 rounded-2xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-primary">{lang === "fr" ? tk.name_fr : tk.name_en}</span>
                    <span className="font-display text-xl font-semibold text-secondary">{tk.price === 0 ? t("Free", "Gratuit") : `$${tk.price}`}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{lang === "fr" ? tk.desc_fr : tk.desc_en}</p>
                </div>
              ))}
            </div>
            <button onClick={rsvp} data-testid="rsvp-btn" className="w-full mt-5 rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:bg-secondary transition-colors flex items-center justify-center gap-2">
              <Check size={18} /> {t("Reserve my spot", "Réserver ma place")}
            </button>
            {ev.spots <= 15 && <p className="text-center text-xs text-secondary font-semibold mt-3">{t(`Only ${ev.spots} spots left`, `Plus que ${ev.spots} places`)}</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
